import { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import matchSubscription from './helpers/matchSubscription';
import getChatEffectSubscriptions from './subscriptions';
import type {
    AGUIEvent,
    ChatEffectRouteParams,
    UseChatEffectsParams,
    UseChatEffectsResult,
} from './types';

const useChatEffects = (_params: UseChatEffectsParams): UseChatEffectsResult => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { pathname } = useLocation();
    const routeParams = useParams();
    const pathnameRef = useRef(pathname);
    const routeParamsRef = useRef(routeParams as ChatEffectRouteParams);
    const subscriptionsRef = useRef(getChatEffectSubscriptions({}));
    const toolNameByCallIdRef = useRef<Record<string, string>>({});
    const toolArgsByCallIdRef = useRef<Record<string, NonNullable<AGUIEvent['args']>>>({});

    pathnameRef.current = pathname;
    routeParamsRef.current = routeParams as ChatEffectRouteParams;

    const handleChatEvent = useCallback<UseChatEffectsResult>(
        event => {
            const activePathname = pathnameRef.current;
            const activeRouteParams = routeParamsRef.current;
            if (
                event.type === 'TOOL_CALL_START' &&
                typeof event.callId === 'string' &&
                typeof event.toolName === 'string'
            ) {
                toolNameByCallIdRef.current[event.callId] = event.toolName;
            }
            if (
                event.type === 'TOOL_CALL_START' &&
                typeof event.callId === 'string' &&
                event.args !== undefined
            ) {
                toolArgsByCallIdRef.current[event.callId] = event.args;
            }
            if (
                event.type === 'TOOL_CALL_ARGS' &&
                typeof event.callId === 'string' &&
                event.args !== undefined
            ) {
                toolArgsByCallIdRef.current[event.callId] = event.args;
            }
            const resolvedToolName =
                typeof event.toolName === 'string'
                    ? event.toolName
                    : typeof event.callId === 'string'
                      ? toolNameByCallIdRef.current[event.callId]
                      : undefined;
            const resolvedArgs =
                event.args !== undefined
                    ? event.args
                    : typeof event.callId === 'string'
                      ? toolArgsByCallIdRef.current[event.callId]
                      : undefined;
            const eventWithResolvedToolName =
                resolvedToolName && event.toolName !== resolvedToolName
                    ? {
                          ...event,
                          toolName: resolvedToolName,
                      }
                    : event;
            const eventWithResolvedData =
                resolvedArgs !== undefined && eventWithResolvedToolName.args === undefined
                    ? {
                          ...eventWithResolvedToolName,
                          args: resolvedArgs,
                      }
                    : eventWithResolvedToolName;

            for (const subscription of subscriptionsRef.current) {
                const matchResult = matchSubscription({
                    subscription,
                    pathname: activePathname,
                    event: eventWithResolvedData,
                    routeParams: activeRouteParams,
                });

                if (!matchResult.matched) {
                    continue;
                }

                try {
                    subscription.action({
                        event: eventWithResolvedData,
                        navigate,
                        queryClient,
                        toast,
                        routeParams: activeRouteParams,
                        matchParams: matchResult.matchParams,
                    });
                } catch (error) {
                    console.error(`Chat effect subscription failed (${subscription.id}):`, error);
                }
            }

            if (event.type === 'TOOL_CALL_RESULT' && typeof event.callId === 'string') {
                delete toolNameByCallIdRef.current[event.callId];
                delete toolArgsByCallIdRef.current[event.callId];
            }
        },
        [navigate, queryClient]
    );

    return handleChatEvent;
};

export default useChatEffects;
